import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { QUERY_KEYS } from '@/hooks/use-queries'
import type { CreateResponse } from '@/lib/types'

import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ChonkyButton } from '@/components/shared/chonky-button'
import { Plus, CheckCircle2, Copy } from 'lucide-react'

const createMerchantSchema = z.object({
  merchantName: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  merchantSlug: z
    .string()
    .min(2)
    .regex(/^[a-z0-9-]+$/, 'Apenas letras minúsculas, números e hífens'),
  adminName: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  adminEmail: z.string().email('E-mail inválido'),
  adminPassword: z.string().min(8, 'Senha deve ter pelo menos 8 caracteres'),
})

type CreateMerchantForm = z.infer<typeof createMerchantSchema>

export function CreateMerchantDialog() {
  const [open, setOpen] = useState(false)
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null)
  const queryClient = useQueryClient()

  const form = useForm<CreateMerchantForm>({
    resolver: zodResolver(createMerchantSchema),
    defaultValues: {
      merchantName: '',
      merchantSlug: '',
      adminName: '',
      adminEmail: '',
      adminPassword: '', // TODO: Could generate a random one
    },
  })

  // Auto-generate slug from name
  const updateSlug = (name: string) => {
    const slug = name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '')

    // Only update if slug hasn't been manually edited or is empty
    const currentSlug = form.getValues('merchantSlug')
    if (!currentSlug || form.formState.dirtyFields.merchantSlug === undefined) {
      form.setValue('merchantSlug', slug)
    }
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CreateMerchantForm) => api.backoffice.createMerchant(data),
    onSuccess: (response: CreateResponse) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.backoffice.merchants })

      const email = response.admin?.email
      const password = response.tempPassword

      if (password) {
        setCredentials({ email: email || '', password })
        toast.success('Lojista criado com sucesso!', { duration: 5000 })
      } else {
        toast.success('Lojista criado com sucesso!', {
          description: `Um e-mail de convite foi enviado para ${email}.`,
        })
        handleClose()
      }
    },
    onError: (error: Error) => {
      toast.error('Erro ao criar lojista', {
        description: error.message || 'Verifique os dados e tente novamente.',
      })
    },
  })

  const onSubmit = (data: CreateMerchantForm) => {
    mutate(data)
  }

  const handleClose = () => {
    setOpen(false)
    setCredentials(null)
    form.reset()
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(val) => {
        if (val) setOpen(true)
        else handleClose()
      }}
    >
      <SheetTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Novo Lojista
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg">
        {credentials ? (
          <div className="py-6 flex flex-col items-center text-center space-y-4">
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 mb-2">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <SheetHeader className="border-b-0 px-0 pt-0 pb-0">
              <SheetTitle className="text-center">Lojista criado com sucesso!</SheetTitle>
              <SheetDescription className="text-center">
                As credenciais de administração foram geradas com sucesso.
              </SheetDescription>
            </SheetHeader>

            <div className="w-full bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3 mt-4">
              <div className="space-y-1 text-left">
                <label className="text-xs font-medium text-slate-500 uppercase">E-mail</label>
                <div className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className="text-sm font-mono">{credentials.email}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      navigator.clipboard.writeText(credentials.email)
                      toast.success('E-mail copiado!')
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              <div className="space-y-1 text-left">
                <label className="text-xs font-medium text-slate-500 uppercase">Senha</label>
                <div className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className="text-sm font-mono">{credentials.password}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => {
                      navigator.clipboard.writeText(credentials.password)
                      toast.success('Senha copiada!')
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="w-full pt-4">
              <ChonkyButton onClick={handleClose} className="w-full">
                Concluir
              </ChonkyButton>
            </div>
          </div>
        ) : (
          <>
            <SheetHeader>
              <SheetTitle>Novo Lojista</SheetTitle>
              <SheetDescription>
                Crie uma nova organização e convide o administrador inicial.
              </SheetDescription>
            </SheetHeader>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="flex flex-col flex-1 overflow-hidden"
              >
                <SheetBody className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="merchantName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome da Empresa</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Ex: Nike Store"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e)
                                updateSlug(e.target.value)
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="merchantSlug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Slug (URL)</FormLabel>
                          <FormControl>
                            <Input placeholder="nike-store" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium mb-3">Administrador Inicial</h4>
                    <FormField
                      control={form.control}
                      name="adminName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome do Responsável</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex: Phil Knight" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid grid-cols-2 gap-4 mt-4">
                      <FormField
                        control={form.control}
                        name="adminEmail"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>E-mail</FormLabel>
                            <FormControl>
                              <Input placeholder="phil@nike.com" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="adminPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Senha Inicial</FormLabel>
                            <FormControl>
                              <Input type="password" placeholder="********" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </SheetBody>

                <SheetFooter>
                  <ChonkyButton variant="secondary" type="button" onClick={() => setOpen(false)}>
                    Cancelar
                  </ChonkyButton>
                  <ChonkyButton type="submit" disabled={isPending}>
                    {isPending ? 'Criando e Enviando...' : 'Criar Lojista'}
                  </ChonkyButton>
                </SheetFooter>
              </form>
            </Form>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
